import { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Box, Button, CircularProgress, TextField, Typography } from '@mui/material'
import { toast } from 'react-toastify'
import {
  confirmDeskMobileVerification,
  getDeskInboundInfo,
  startDeskMobileVerification,
} from 'src/clinic/services/deskCallService'

// "Or call from your phone": shows the brand's inbound desk number and, when the brand requires it,
// lets the assistant verify their assignment mobile with a WhatsApp one-time code so calls from that
// phone are recognised and auto-routed.

export default function DeskCallerVerification({ clinicId }) {
  const [info, setInfo] = useState(null)
  const [loading, setLoading] = useState(false)
  const [mobile, setMobile] = useState('')
  const [sessionId, setSessionId] = useState('')
  const [otp, setOtp] = useState('')
  const [busy, setBusy] = useState(false)
  const [stage, setStage] = useState('idle')

  const load = useCallback(async () => {
    if (!clinicId) return
    setLoading(true)
    const result = await getDeskInboundInfo({ clinicId })
    setInfo(result)
    setLoading(false)
  }, [clinicId])

  useEffect(() => {
    load()
  }, [load])

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
        <CircularProgress size={12} />
        <Typography sx={{ fontSize: '11px', color: 'rgba(0,0,0,0.5)' }}>Checking the desk number…</Typography>
      </Box>
    )
  }
  if (!info?.ok || !info?.inboundEnabled || !info?.inboundNumber) {
    return null
  }

  const needsVerification = info.requireVerifiedCallerMobile && !info.assignmentMobileVerified && !info.configuredOnBrand

  const handleStart = async () => {
    setBusy(true)
    const result = await startDeskMobileVerification({ clinicId, mobile: mobile || undefined })
    setBusy(false)
    if (!result?.ok) {
      toast.error(result?.message)
      return
    }
    setSessionId(result?.sessionId || '')
    setStage('otp')
    toast.info(`Code sent on WhatsApp to ${result?.mobileMasked || 'your mobile'}.`)
  }

  const handleConfirm = async () => {
    setBusy(true)
    const result = await confirmDeskMobileVerification({ clinicId, sessionId, otp })
    setBusy(false)
    if (!result?.ok) {
      toast.error(result?.message)
      return
    }
    toast.success('Mobile verified. Calls from it will now be routed automatically.')
    setStage('idle')
    setOtp('')
    load()
  }

  return (
    <Box sx={{ mt: 1.5, pt: 1.25, borderTop: '1px dashed rgba(0,0,0,0.12)' }}>
      <Typography sx={{ fontSize: '12px', fontWeight: 700 }}>Or call from your phone</Typography>
      <Typography sx={{ fontSize: '12px', color: 'rgba(0,0,0,0.65)' }}>
        Dial <strong>{info.inboundNumber}</strong>
        {info.assignmentMobileMasked ? ` from ${info.assignmentMobileMasked}` : ''} and you will be routed to whoever is on
        duty.
      </Typography>
      {needsVerification ? (
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography sx={{ fontSize: '11px', color: '#b45309' }}>
            Verify your mobile once so the desk number recognises your calls.
          </Typography>
          {stage === 'otp' ? (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size="small"
                label="6-digit code"
                value={otp}
                onChange={(event) => setOtp(event.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                inputProps={{ inputMode: 'numeric' }}
                sx={{ flex: 1 }}
              />
              <Button size="small" variant="contained" disabled={busy || otp.length !== 6} onClick={handleConfirm} sx={{ textTransform: 'none' }}>
                Confirm
              </Button>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: 1 }}>
              {!info.assignmentMobileMasked ? (
                <TextField
                  size="small"
                  label="Your mobile"
                  value={mobile}
                  onChange={(event) => setMobile(event.target.value)}
                  inputProps={{ inputMode: 'tel' }}
                  sx={{ flex: 1 }}
                />
              ) : null}
              <Button size="small" variant="outlined" disabled={busy} onClick={handleStart} sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>
                {busy ? <CircularProgress size={14} /> : 'Verify my mobile'}
              </Button>
            </Box>
          )}
        </Box>
      ) : info.assignmentMobileVerified || info.configuredOnBrand ? (
        <Typography sx={{ fontSize: '11px', color: '#047857', mt: 0.5 }}>Your phone is recognised.</Typography>
      ) : null}
    </Box>
  )
}

DeskCallerVerification.propTypes = {
  clinicId: PropTypes.string,
}
